/**
 * External semantic regression fixtures. These expected values are copied as
 * small factual assertions from official iztro documentation examples, not
 * inferred by this project.
 */
export interface ExternalCalculationFixture {
  id: string;
  sourceId: string;
  solarInput: {
    date: string;
    timeIndex: number;
    gender: 'male' | 'female';
    fixLeap: boolean;
  };
  equivalentLunarInput?: {
    date: string;
    timeIndex: number;
    gender: 'male' | 'female';
    isLeapMonth: boolean;
    fixLeap: boolean;
  };
  expected: {
    solarDate: string;
    lunarDateLabel: string;
    chineseDate?: string;
    timeLabel: string;
    timeRange: string;
    zodiac: string;
    soulPalaceBranch: string;
    bodyPalaceBranch: string;
    soulStarLabel: string;
    bodyStarLabel: string;
    fiveElementsClass: string;
    palaceAssertions: readonly {
      palaceLabel: string;
      earthlyBranch: string;
      majorStars: readonly { label: string; brightness?: string }[];
    }[];
  };
}

export const IZTRO_OFFICIAL_FIXTURES: readonly ExternalCalculationFixture[] = [
  {
    id: 'F001-IZTRO-OFFICIAL-2000-08-16-FEMALE-YIN',
    sourceId: 'SRC-IZTRO-DOC-ASTROLABE-EXAMPLE-001',
    solarInput: {
      date: '2000-8-16',
      timeIndex: 2,
      gender: 'female',
      fixLeap: true,
    },
    equivalentLunarInput: {
      date: '2000-7-17',
      timeIndex: 2,
      gender: 'female',
      isLeapMonth: false,
      fixLeap: true,
    },
    expected: {
      solarDate: '2000-8-16',
      lunarDateLabel: '二〇〇〇年七月十七',
      chineseDate: '庚辰 甲申 丙午 庚寅',
      timeLabel: '寅時',
      timeRange: '03:00~05:00',
      zodiac: '龍',
      soulPalaceBranch: '午',
      bodyPalaceBranch: '戌',
      soulStarLabel: '破軍',
      bodyStarLabel: '文昌',
      fiveElementsClass: '木三局',
      palaceAssertions: [
        {
          palaceLabel: '財帛',
          earthlyBranch: '寅',
          majorStars: [
            { label: '武曲', brightness: '得' },
            { label: '天相', brightness: '廟' },
            { label: '天馬' },
          ],
        },
        {
          palaceLabel: '子女',
          earthlyBranch: '卯',
          majorStars: [
            { label: '太陽', brightness: '廟' },
            { label: '天梁', brightness: '廟' },
          ],
        },
        {
          palaceLabel: '夫妻',
          earthlyBranch: '辰',
          majorStars: [{ label: '七殺', brightness: '廟' }],
        },
      ],
    },
  },
] as const;

export type PropertyFixtureKind =
  | 'leap_month_fix'
  | 'late_rat_day_divide'
  | 'day_divide_invariance'
  | 'year_boundary'
  | 'horoscope_boundary'
  | 'horoscope_scope';

export interface PropertyCalculationFixture {
  id: string;
  sourceId: string;
  kind: PropertyFixtureKind;
  status: 'property_only_pending_adapter';
  input: Readonly<Record<string, string | number | boolean>>;
  variants: readonly Readonly<Record<string, string | number | boolean>>[];
  expectedProperties: readonly string[];
  prohibitedAssertions: readonly string[];
}

/**
 * Boundary fixtures whose expected values are deliberately expressed as
 * documented properties rather than guessed palace/star outputs. They become
 * executable integration assertions only after IztroAdapter is available.
 */
export const IZTRO_BOUNDARY_PROPERTY_FIXTURES: readonly PropertyCalculationFixture[] = [
  Object.freeze({
    id: 'F004-LEAP-MONTH-FIRST-HALF-FIXLEAP',
    sourceId: 'SRC-IZTRO-DOC-001',
    kind: 'leap_month_fix',
    status: 'property_only_pending_adapter',
    input: Object.freeze({ lunarDate: '2023-2-10', timeIndex: 6, gender: 'female', isLeapMonth: true }),
    variants: Object.freeze([
      Object.freeze({ fixLeap: true }),
      Object.freeze({ fixLeap: false }),
    ]),
    expectedProperties: Object.freeze([
      'With fixLeap=true, a leap-month day in days 1-15 follows iztro leap-month first-half adjustment semantics.',
      'With fixLeap=false, the leap month is not split by the fixLeap first-half/second-half rule.',
    ]),
    prohibitedAssertions: Object.freeze([
      'Do not assert exact palace/star positions until IztroAdapter executes the pinned engine.',
    ]),
  }),
  Object.freeze({
    id: 'F005-LEAP-MONTH-SECOND-HALF-FIXLEAP',
    sourceId: 'SRC-IZTRO-DOC-001',
    kind: 'leap_month_fix',
    status: 'property_only_pending_adapter',
    input: Object.freeze({ lunarDate: '2023-2-20', timeIndex: 6, gender: 'female', isLeapMonth: true }),
    variants: Object.freeze([
      Object.freeze({ fixLeap: true }),
      Object.freeze({ fixLeap: false }),
    ]),
    expectedProperties: Object.freeze([
      'With fixLeap=true, a leap-month day from day 16 onward follows iztro leap-month second-half adjustment semantics.',
      'The first-half and second-half property fixtures must remain distinct regression cases.',
    ]),
    prohibitedAssertions: Object.freeze([
      'Do not infer that F004 and F005 must have any specific palace/star difference without engine output.',
    ]),
  }),
  Object.freeze({
    id: 'F006-LATE-RAT-DAYDIVIDE-FORWARD-VS-CURRENT',
    sourceId: 'SRC-IZTRO-DOC-001',
    kind: 'late_rat_day_divide',
    status: 'property_only_pending_adapter',
    input: Object.freeze({ solarDate: '2024-6-15', timeIndex: 12, gender: 'female' }),
    variants: Object.freeze([
      Object.freeze({ dayDivide: 'forward' }),
      Object.freeze({ dayDivide: 'current' }),
    ]),
    expectedProperties: Object.freeze([
      'timeIndex=12 is the late Rat-hour boundary case and must be tested under both dayDivide variants.',
      'forward and current have different documented date-assignment semantics for late Rat hour.',
    ]),
    prohibitedAssertions: Object.freeze([
      'Do not assert which palaces/stars change until the pinned engine is executed.',
    ]),
  }),
  Object.freeze({
    id: 'F007-EARLY-RAT-DAYDIVIDE-INVARIANCE',
    sourceId: 'SRC-IZTRO-DOC-001',
    kind: 'day_divide_invariance',
    status: 'property_only_pending_adapter',
    input: Object.freeze({ solarDate: '2024-6-15', timeIndex: 0, gender: 'female' }),
    variants: Object.freeze([
      Object.freeze({ dayDivide: 'forward' }),
      Object.freeze({ dayDivide: 'current' }),
    ]),
    expectedProperties: Object.freeze([
      'The dayDivide distinction is specifically a late Rat-hour rule; early Rat hour is retained as a control case.',
      'Integration tests should flag any unexpected semantic difference caused solely by dayDivide at timeIndex=0.',
    ]),
    prohibitedAssertions: Object.freeze([
      'This property is an integration invariant candidate, not a substitute for executing iztro.',
    ]),
  }),
  Object.freeze({
    id: 'F008-NON-RAT-DAYDIVIDE-INVARIANCE',
    sourceId: 'SRC-IZTRO-DOC-001',
    kind: 'day_divide_invariance',
    status: 'property_only_pending_adapter',
    input: Object.freeze({ solarDate: '2024-6-15', timeIndex: 6, gender: 'female' }),
    variants: Object.freeze([
      Object.freeze({ dayDivide: 'forward' }),
      Object.freeze({ dayDivide: 'current' }),
    ]),
    expectedProperties: Object.freeze([
      'A non-Rat-hour case is retained as a negative control for dayDivide contamination.',
      'Integration tests should flag a dayDivide-only semantic change outside late Rat hour unless upstream semantics change and are reviewed.',
    ]),
    prohibitedAssertions: Object.freeze([
      'Do not turn this control into a universal astrology rule independent of the pinned engine contract.',
    ]),
  }),
  Object.freeze({
    id: 'F009-YEAR-DIVIDE-LUNAR-NEW-YEAR-VS-LICHUN',
    sourceId: 'SRC-IZTRO-DOC-001',
    kind: 'year_boundary',
    status: 'property_only_pending_adapter',
    input: Object.freeze({ solarDate: '2024-2-6', timeIndex: 6, gender: 'female' }),
    variants: Object.freeze([
      Object.freeze({ yearDivide: 'normal' }),
      Object.freeze({ yearDivide: 'exact' }),
    ]),
    expectedProperties: Object.freeze([
      'The selected date lies after Li Chun but before Lunar New Year in 2024, making it a deliberate yearDivide boundary case.',
      'normal uses Lunar New Year while exact uses Li Chun for the documented year boundary.',
    ]),
    prohibitedAssertions: Object.freeze([
      'Do not precompute an exact chart delta; assert only the boundary semantics until adapter execution.',
    ]),
  }),
  Object.freeze({
    id: 'F010-HOROSCOPE-DIVIDE-LUNAR-NEW-YEAR-VS-LICHUN',
    sourceId: 'SRC-IZTRO-DOC-001',
    kind: 'horoscope_boundary',
    status: 'property_only_pending_adapter',
    input: Object.freeze({ solarDate: '2024-2-6', timeIndex: 6, gender: 'female' }),
    variants: Object.freeze([
      Object.freeze({ horoscopeDivide: 'normal' }),
      Object.freeze({ horoscopeDivide: 'exact' }),
    ]),
    expectedProperties: Object.freeze([
      'The same 2024-02-06 boundary date is used to isolate horoscopeDivide semantics from yearDivide semantics.',
      'normal and exact must be exercised separately for horoscope year/month stem-branch boundary handling.',
    ]),
    prohibitedAssertions: Object.freeze([
      'Do not claim a specific annual/monthly horoscope result until IztroAdapter integration runs.',
    ]),
  }),
] as const;


export const IZTRO_HOROSCOPE_PROPERTY_FIXTURES: readonly PropertyCalculationFixture[] = [
  Object.freeze({
    id: 'F011-HOROSCOPE-DECADAL-CONTRACT',
    sourceId: 'SRC-IZTRO-DOC-002',
    kind: 'horoscope_scope',
    status: 'property_only_pending_adapter',
    input: Object.freeze({ birthSolarDate: '2000-8-16', birthTimeIndex: 2, gender: 'female', targetSolarDate: '2023-10-26', targetTimeIndex: 2, scope: 'decadal' }),
    variants: Object.freeze([Object.freeze({ scope: 'decadal' })]),
    expectedProperties: Object.freeze([
      'horoscope() must expose a decadal cycle item for the target date.',
      'The decadal scope must support cycle-palace lookup and, in the pinned 2.6.0 contract, moving-star and Four-Transformation data.',
    ]),
    prohibitedAssertions: Object.freeze(['Do not guess the decadal palace index, stem/branch, moving stars, or transformations before adapter execution.']),
  }),
  Object.freeze({
    id: 'F012-HOROSCOPE-YEARLY-CONTRACT',
    sourceId: 'SRC-IZTRO-DOC-002',
    kind: 'horoscope_scope',
    status: 'property_only_pending_adapter',
    input: Object.freeze({ birthSolarDate: '2000-8-16', birthTimeIndex: 2, gender: 'female', targetSolarDate: '2023-10-26', targetTimeIndex: 2, scope: 'yearly' }),
    variants: Object.freeze([Object.freeze({ scope: 'yearly' })]),
    expectedProperties: Object.freeze([
      'horoscope() must expose a yearly cycle item for the target date.',
      'The yearly scope must expose moving-star and Four-Transformation data and remain addressable by cycle-palace analysis APIs.',
    ]),
    prohibitedAssertions: Object.freeze(['Do not guess yearly palace names, moving stars, or transformation targets before adapter execution.']),
  }),
  Object.freeze({
    id: 'F013-HOROSCOPE-MONTHLY-CONTRACT',
    sourceId: 'SRC-IZTRO-DOC-002',
    kind: 'horoscope_scope',
    status: 'property_only_pending_adapter',
    input: Object.freeze({ birthSolarDate: '2000-8-16', birthTimeIndex: 2, gender: 'female', targetSolarDate: '2023-10-26', targetTimeIndex: 2, scope: 'monthly' }),
    variants: Object.freeze([Object.freeze({ scope: 'monthly' })]),
    expectedProperties: Object.freeze([
      'horoscope() must expose a monthly cycle item for the target date.',
      'Pinned iztro 2.6.0 is newer than v2.4.4, so monthly moving-star data and Four Transformations are part of the integration contract.',
    ]),
    prohibitedAssertions: Object.freeze(['Do not copy stale pre-v2.4.4 documentation that says monthly cycles lack moving stars.']),
  }),
  Object.freeze({
    id: 'F014-HOROSCOPE-DAILY-CONTRACT',
    sourceId: 'SRC-IZTRO-DOC-002',
    kind: 'horoscope_scope',
    status: 'property_only_pending_adapter',
    input: Object.freeze({ birthSolarDate: '2000-8-16', birthTimeIndex: 2, gender: 'female', targetSolarDate: '2023-10-26', targetTimeIndex: 2, scope: 'daily' }),
    variants: Object.freeze([Object.freeze({ scope: 'daily' })]),
    expectedProperties: Object.freeze([
      'horoscope() must expose a daily cycle item for the target date.',
      'Pinned iztro 2.6.0 must provide daily moving-star data and Four Transformations.',
    ]),
    prohibitedAssertions: Object.freeze(['Do not guess the daily cycle position or stars before adapter execution.']),
  }),
  Object.freeze({
    id: 'F015-HOROSCOPE-HOURLY-CONTRACT',
    sourceId: 'SRC-IZTRO-DOC-002',
    kind: 'horoscope_scope',
    status: 'property_only_pending_adapter',
    input: Object.freeze({ birthSolarDate: '2000-8-16', birthTimeIndex: 2, gender: 'female', targetSolarDate: '2023-10-26', targetTimeIndex: 2, scope: 'hourly' }),
    variants: Object.freeze([Object.freeze({ scope: 'hourly', targetTimeIndex: 2 })]),
    expectedProperties: Object.freeze([
      'horoscope() must expose an hourly cycle item when a target time index is supplied.',
      'Pinned iztro 2.6.0 must provide hourly moving-star data and Four Transformations.',
    ]),
    prohibitedAssertions: Object.freeze(['Do not infer hourly cycle results from daily results; execute the pinned engine.']),
  }),
  Object.freeze({
    id: 'F016-HOROSCOPE-MINOR-AGE-CONTRACT',
    sourceId: 'SRC-IZTRO-DOC-002',
    kind: 'horoscope_scope',
    status: 'property_only_pending_adapter',
    input: Object.freeze({ birthSolarDate: '2000-8-16', birthTimeIndex: 2, gender: 'female', targetSolarDate: '2023-10-26', targetTimeIndex: 2, scope: 'minor' }),
    variants: Object.freeze([Object.freeze({ ageDivide: 'normal' }), Object.freeze({ ageDivide: 'birthday' })]),
    expectedProperties: Object.freeze([
      'horoscope() must expose minor-cycle age metadata and agePalace() must resolve the Minor Cycle palace when available.',
      'The Minor Cycle has no moving-star set and no independent cycle transformations; related transformations are derived from the palace heavenly stem.',
      'ageDivide controls the nominal-age boundary and must be integration-tested as a separate configuration dimension.',
    ]),
    prohibitedAssertions: Object.freeze(['Do not represent Minor Cycle as having the same moving-star payload as yearly/monthly/daily/hourly scopes.']),
  }),
] as const;

export function boundaryFixtureById(id: string): PropertyCalculationFixture | undefined {
  return IZTRO_BOUNDARY_PROPERTY_FIXTURES.find((fixture) => fixture.id === id);
}
